import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { CurrencySelect } from '@/components/CurrencySelect';
import { Button } from '@/components/ui/button';
import { accountsApi } from '@/lib/api/accounts';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

interface AccountCurrencySettingsProps {
  accountId: string;
  currentDefaultCurrency?: string;
}

export function AccountCurrencySettings({
  accountId,
  currentDefaultCurrency = 'CZK',
}: AccountCurrencySettingsProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [selectedCurrency, setSelectedCurrency] = useState(currentDefaultCurrency);

  const { data: account } = useQuery({
    queryKey: ['account', accountId],
    queryFn: () => accountsApi.get(accountId),
    enabled: !!accountId,
  });

  const updateMutation = useMutation({
    mutationFn: (defaultCurrency: string) =>
      accountsApi.update(accountId, { defaultCurrency }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['account', accountId] });
      queryClient.invalidateQueries({ queryKey: ['/me'] });
      toast.success(t('account.settings.currency.saved') ?? 'Default currency saved');
    },
    onError: () => {
      toast.error(t('account.settings.currency.saveFailed') ?? 'Failed to save');
    },
  });

  const currency = account?.defaultCurrency ?? currentDefaultCurrency;

  useEffect(() => {
    setSelectedCurrency(currency);
  }, [currency]);

  const isDirty = selectedCurrency !== currency;

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('account.settings.currency.title') ?? 'Account default currency'}</CardTitle>
        <CardDescription>
          {t('account.settings.currency.description') ?? 'Used when creating new inquiries and for displaying amounts.'}
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-wrap items-end gap-4">
        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium">
            {t('account.settings.currency.label') ?? 'Default currency'}
          </label>
          <CurrencySelect
            value={selectedCurrency}
            onValueChange={setSelectedCurrency}
          />
        </div>
        {isDirty && (
          <Button
            onClick={() => updateMutation.mutate(selectedCurrency)}
            disabled={updateMutation.isPending}
          >
            {updateMutation.isPending
              ? t('generic.saving')
              : t('generic.save')}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
